import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { Image } from 'Components/atoms/Image';
import { Price } from 'Components/atoms/Price';
import { Link } from 'Components/molecules/Link';
import { withPolaris } from 'Components/protons/Polaris';
import { asyncTrackLinkClick } from 'SharedHelpers/tracking';

import './_style.scss';

/**
 * StockDealsCard component renders a card with product information including image, title, prices, and links.
 */
const StockDealsCard = ({
  context: { cssPrefix },
  extraClassNames,
  cashPrice: {
    min: cashPrice,
    prefixText: cashPrefixText,
  },
  image: {
    alt,
    height,
    src,
    width,
  },
  marketplaceLink: {
    label: marketplaceLabel,
    tracking,
    url: marketplaceUrl,
  },
  tagline,
  techData,
  title,
}) => {
  /**
   * Handles the marketplace link click event.
   * Pushes the tracking object to the dataLayer.
   * @example of tracking object:
   * {
   *  event: 'marketplace.button_click',
   *  'event.type': 'GYCCustom',
   *  'marketplace_button.placement': 'GYCHotDealsBlockCustom',
   *  }
   */
  const handleLinkClick = async (trackingPayload) => {
    if (typeof window !== 'undefined' && tracking && Object.keys(tracking).length > 0) {
      window.dataLayer = window.dataLayer || [];
      await asyncTrackLinkClick(trackingPayload);
    }
  };

  return (
    <div
      className={classNames({
        [`${cssPrefix}__stock-deals-card`]: true,
        ...extraClassNames,
      })}
    >
      <div className={`${cssPrefix}__stock-deals-card--media`}>
        {src && ( // Only render the image if the src is provided, but keep the container for layout purposes
          <Image
            extraClassNames={{
              [`${cssPrefix}__stock-deals-card--image`]: true,
            }}
            disableTransforms
            src={src}
            alt={alt}
            height={height}
            width={width}
          />
        )}
      </div>
      <div className={`${cssPrefix}__stock-deals-card--content`}>
        <div className={`${cssPrefix}__stock-deals-card--info`}>
          <p className={`${cssPrefix}__stock-deals-card--tagline`}>{tagline}</p>
          <p className={`${cssPrefix}__stock-deals-card--title`}>{title}</p>
          {techData && techData.length > 0 && (
            <p className={`${cssPrefix}__stock-deals-card--tech-data`}>
              {techData.filter(item => item && item.value).map((item, index) => (
                <span
                  key={index}
                  className={`${cssPrefix}__stock-deals-card--tech-data-value`}
                  title={`${item.title}: ${item.value}`}
                >
                  {item.value}
                </span>
              ))}
            </p>
          )}
          {cashPrice > 0 && (
            <Price
              extraClassNames={{
                [`${cssPrefix}__stock-deals-card--cash-price`]: true,
              }}
              minOnly
              min={cashPrice}
              prefixText={cashPrefixText}
            />
          )}
        </div>
        {marketplaceUrl && (
          <Link
            extraClassNames={{
              [`${cssPrefix}__stock-deals-card--marketplace-link`]: true,
              '-link-as-button': true,
            }}
            href={marketplaceUrl}
            onClick={async () => { await handleLinkClick(tracking); }}
          >
            {marketplaceLabel}
          </Link>
        )}
      </div>
    </div>
  );
};

StockDealsCard.propTypes = {
  context: PropTypes.shape({
    cssPrefix: PropTypes.string,
  }),
  extraClassNames: PropTypes.shape({}),
  image: PropTypes.shape({
    alt: PropTypes.string,
    height: PropTypes.number,
    src: PropTypes.string,
    width: PropTypes.number,
  }),
  marketplaceLink: PropTypes.shape({
    label: PropTypes.string,
    tracking: PropTypes.shape(),
    url: PropTypes.string,
  }),
  cashPrice: PropTypes.shape({
    min: PropTypes.number,
    prefixText: PropTypes.string,
  }),
  tagline: PropTypes.string,
  title: PropTypes.string,
  techData: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string,
    value: PropTypes.string,
  })),
};

StockDealsCard.defaultProps = {
  context: { cssPrefix: 'polaris' },
  extraClassNames: {},
  image: {
    alt: '',
    height: 0,
    src: '',
    width: 0,
  },
  marketplaceLink: {
    label: '',
    tracking: {},
    url: '',
  },
  cashPrice: {
    min: 0,
    prefixText: '',
  },
  tagline: '',
  title: '',
  techData: [],
};

export default withPolaris(StockDealsCard);
